
import { Autocomplete,Card,CardContent,Checkbox,FormControlLabel,TextField } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useRef,useState } from 'react';
import { useTranslation } from 'react-i18next';
import AttToken from '../helpers/attToken';
import { trackEventMatomo } from '../helpers/matomo';
import Teams from '../service/Teams';
import Users from '../service/Users';
import type { SimpleTeam } from '../types/teams/types';
import type { userType } from '../types/users/types';


type props = {
  callback: Function,
  userLabel: string,
  defaultSelected: string | null
};

type stateProps = {
  value: string,
  label: string
};

const TeamsService = new Teams();
const UsersService = new Users();

export default function UserSelector({ callback, userLabel, defaultSelected }: props) {
  const [selectingByTeam, setSlectingByTeam] = useState(false);
  const [selectedTeam, setSeletedTeam] = useState<SimpleTeam>();
  const [selectedUser, setSelectedUser] = useState<stateProps | null>(null);
  const { t } = useTranslation('translation');

  const results = useQueries({
    queries: [
      {
        queryKey: ['GET_TEAMS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response: SimpleTeam[] = await TeamsService.getSimpleTeams(token);
            return response;
          }
        },
        retry: 5,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      },
      {
        queryKey: ['GET_USERS'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const body = { ...token };
            const result: { users: userType[] } = await UsersService.getAllUsers(body);
            return result.users;
          }
        },
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
      }
    ]
  });

  const userSelectByTeamRef = useRef<HTMLSelectElement>(null);
  const userSelectRef = useRef<HTMLSelectElement>(null);

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <FormControlLabel control={<Checkbox
          onClick={() => trackEventMatomo('seleção de usuários', 'click', 'input', 'edita valor')}
          onChange={(e) => {
            callback('');
            if (userSelectByTeamRef && userSelectByTeamRef.current) {
              userSelectByTeamRef.current.value = '';
            }
            if (userSelectRef && userSelectRef.current) {
              userSelectRef.current.value = '';
            }
            setSeletedTeam(undefined);
            setSelectedUser(null);
            setSlectingByTeam(e.target.checked);
          }}
          checked={selectingByTeam}
        />} label={t('users.userSelector.filterTeams')} />
        <Autocomplete
          onClick={() => trackEventMatomo('seleção de usuários', 'click', 'select', 'seleciona valor')}
          getOptionLabel={(option) => option.label || ''}
          value={selectingByTeam ? { value: selectedTeam?.id, label: selectedTeam?.nome } : { value: '', label: '' }}
          sx={{ marginTop: '10px' }}
          fullWidth
          onChange={(_event, newValue) => {
            callback('');
            if (newValue) {
              setSeletedTeam(results[0].data?.find((team) => team.id === newValue.value));
            } else {
              setSeletedTeam(undefined);
            }
          }}
          options={
            (results[0].data as SimpleTeam[])
              ?.sort((a, b) => a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1)
              ?.map((team) => ({
                label: team?.nome,
                value: team?.id,
              }))
            || []}
          isOptionEqualToValue={(option, value) => option === value}
          disableClearable={!selectingByTeam}
          disabled={!selectingByTeam}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                `${selectingByTeam
                  ? selectedTeam
                    ? `${t('team')} ${selectedTeam?.nome} ${t('selected')}`
                    : t('users.userSelector.selectATeam')
                  : t('users.userSelector.notFilteringTeams')}`}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <span>{option.label}</span>
            </li>)}
          loading={isLoading || isFetching}
        />
        <Autocomplete
          onClick={() => trackEventMatomo('seleção de usuários', 'click', 'select', 'seleciona valor')}
          sx={{ marginTop: '10px' }}
          fullWidth
          onChange={(_event, newValue) => {
            callback(newValue ? newValue.value : '');
            setSelectedUser({
              value: newValue?.value as string,
              label: newValue?.label as string
            });
          }}
          value={selectingByTeam && selectedUser?.label ? { value: selectedUser?.value, label: selectedUser?.label } : null}
          options={
            selectedTeam && selectedTeam.membros && selectedTeam.membros.length > 0
              ? selectedTeam.membros
                ?.sort((a, b) => (a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1))
                ?.map(
                  (membro) => ({
                    label: membro?.nome,
                    value: membro?.id,
                  })
                )
              : []
          }
          disableClearable={!selectingByTeam}
          isOptionEqualToValue={(option, value) => option === value}
          disabled={!selectingByTeam || !selectedTeam}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                `${selectingByTeam
                  ? selectedTeam
                    ? userLabel
                    : t('users.userSelector.selectTeam')
                  : t('users.userSelector.notFilteringTeams')}`}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <span>{option.label}</span>
            </li>)}
          loading={isLoading || isFetching}
        />
        <Autocomplete
          onClick={() => trackEventMatomo('seleção de usuários', 'click', 'select', 'seleciona valor')}
          sx={{ marginTop: '10px' }}
          value={selectedUser?.value ? results[1].data?.find((ele) => ele.id === selectedUser.value) : null}
          fullWidth
          options={results[1].data?.sort((a, b) => (a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1)).filter((ele) => ele.status === 'ativo') || []}
          getOptionLabel={(user) => user.nome || ''}
          onChange={(event, value) => {
            if (value) {
              callback(value ? value.id : '');
              setSelectedUser({
                value: value?.id || '',
                label: results[1].data?.find((ele) => ele.id === value.id)?.nome || ''
              });
            }
          }}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          disabled={selectingByTeam}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${selectingByTeam ? t('users.userSelector.filteringTeams') : userLabel}`}
              variant="outlined"
              required
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <span>{option.nome}</span>
            </li>)}
          defaultValue={results[1].data?.find((user) => user.id === defaultSelected) || null}
          loading={isLoading || isFetching}
        />
      </CardContent>
    </Card>
  );
}
